"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/utils/api";
import { queryKeys } from "@/lib/query";
import type {
  AppNotification,
  NotificationsResponse,
} from "@/types/notification";

const NOTIFICATION_POLL_INTERVAL_MS = 30_000;

async function fetchNotifications() {
  return apiRequest<NotificationsResponse>(
    "/api/notifications?page=1&pageSize=25",
  );
}

export function useNotifications(activeOrganizationId?: string | null) {
  return useQuery({
    queryKey: queryKeys.notifications.list(activeOrganizationId),
    queryFn: fetchNotifications,
    enabled: Boolean(activeOrganizationId),
    refetchInterval: NOTIFICATION_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useMarkNotificationRead(activeOrganizationId?: string | null) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.notifications.list(activeOrganizationId);

  return useMutation({
    mutationFn: (notificationId: string) =>
      apiRequest<{ notification: AppNotification }>(
        `/api/notifications/${encodeURIComponent(notificationId)}/read`,
        { method: "PUT" },
      ),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationsResponse>(queryKey);

      queryClient.setQueryData<NotificationsResponse>(queryKey, (current) => {
        if (!current) return current;
        const now = new Date().toISOString();
        let changed = false;
        const docs = current.docs.map((notification) => {
          if (
            notification.id !== notificationId ||
            notification.readAt
          ) {
            return notification;
          }
          changed = true;
          return { ...notification, readAt: now };
        });
        return changed
          ? {
              ...current,
              docs,
              unreadCount: Math.max(0, current.unreadCount - 1),
            }
          : current;
      });

      return { previous };
    },
    onError: (_error, _notificationId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useMarkAllNotificationsRead(
  activeOrganizationId?: string | null,
) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.notifications.list(activeOrganizationId);

  return useMutation({
    mutationFn: () =>
      apiRequest<{ updatedCount: number }>("/api/notifications/read-all", {
        method: "POST",
      }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationsResponse>(queryKey);
      const now = new Date().toISOString();

      queryClient.setQueryData<NotificationsResponse>(queryKey, (current) =>
        current
          ? {
              ...current,
              docs: current.docs.map((notification) => ({
                ...notification,
                readAt: notification.readAt || now,
              })),
              unreadCount: 0,
            }
          : current,
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function isNotificationUnread(notification: AppNotification) {
  return !notification.readAt;
}
